import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { round } from '@turf/turf';
import { skipWhile } from 'rxjs/operators';

import { setActiveSite, setActiveSiteSuccess, updateSiteFailure } from '@store/site/site.actions';

import { SiteService } from '@core/api/site.service';
import { UserService } from '@core/api/user.service';
import { FormatHelperService } from '@core/helper/format-helper.service';
import { SiteHelperService } from '@core/helper/site.service';
import * as fromApp from '@store';
import { selectActiveSite } from '@store/site/site.selectors';
import { DialogChangeApplicantComponent } from '@shared/dialogs/dialog-change-owner/dialog-change-applicant.component';
import { EnrollmentGroup, Parcel, Program, Site, User } from '@shared/models';
import { DialogYesNoComponent } from '@shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { DialogEditCustomOfferComponent } from '@shared/dialogs/dialog-edit-custom-offer/dialog-edit-custom-offer.component';
import { EnrollmentGroupService } from '@core/api/enrollment-group.service';
import { DialogSelectOptionComponent } from '@shared/dialogs/dialog-select-option/dialog-select-option.component';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { DialogFormComponent } from '@shared/dialogs/dialog-form/dialog-form.component';
import { FormControl, Validators } from '@angular/forms';
import { RegexConstants } from '@shared/constants/regex.constants';
import { ToastService } from '@shared/services/toast.service';
import { ToastLevel } from '@shared/models/toast-message.module';

@Component({
  selector: 'fc-site-detail-overview',
  templateUrl: './site-detail-overview.component.html',
  styleUrls: ['./site-detail-overview.component.scss'],
  standalone: false
})
export class SiteDetailOverviewComponent implements OnInit {
  tooBig: boolean = false;
  tooSmall: boolean = false;
  site: Site = null;
  programs: Program[];
  parcelData: Parcel[] = [];
  destroyRef = inject(DestroyRef);

  twentyYearMark: string = null;
  twentyYearProjection: string = 'N/A';

  admins: User[] = [];
  assignedAdminId: number = -1;

  assignedAdminChange($event: MatSelectChange) {
    this.siteService.setAssignedAdmin(this.site.id, $event.value).subscribe((site: Site) => (this.site = site));
  }

  @Input()
  isSuperAdmin = false;

  parcelArea = 0;
  userName: string;
  delegateName = '--';
  acres: string;
  hasOutdatedParcels: boolean = false;
  publicOverlap: number = 0;
  @Input() readOnly: boolean = true;

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly router: Router,
    public dialog: MatDialog,
    readonly userService: UserService,
    readonly siteService: SiteService,
    readonly toastService: ToastService,
    readonly spinnerService: SpinnerService,
    readonly enrollmentGroupService: EnrollmentGroupService,
    readonly siteHelper: SiteHelperService,
    readonly formatHelper: FormatHelperService
  ) {}

  ngOnInit() {
    this.store
      .select(selectActiveSite)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        skipWhile(site => site === null)
      )
      .subscribe(site => {
        this.site = site;
        if (!this.readOnly && site.submittedDate !== null && site.activationDate === null) {
          this.siteService.siteNeedsRestart(this.site.id).subscribe(restart => {
            if (restart) {
              this.hasOutdatedParcels = true;
            }
          });
          this.siteService.sitePublicOverlap(this.site.id).subscribe(overlapAcres => {
            if (overlapAcres) {
              this.publicOverlap = overlapAcres;
            }
          });
        }
        this.tooBig = this.site.area > 5000;
        this.tooSmall = this.site.area < 40;
        this.parcelData = this.site.versions[0].parcels;
        if (this.parcelData) {
          this.parcelArea = 0;
          this.parcelData.forEach((parcel: Parcel) => {
            this.parcelArea += parcel.getAreaInAcres();
          });
        }

        this.userName = this.site.applicant.getDisplayName();
        if (this.site.applicant.delegate) {
          this.delegateName = this.site.applicant.delegate.getDisplayName();
        }

        this.acres = this.site.area
          ? this.site.area.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          })
          : '0';

        this.twentyYearMark = this.site.getYearMarkString();
        this.twentyYearProjection = this.formatHelper.formatMinMaxValuation(this.site.getTwentyYearProjection());
        if (!this.readOnly) {
          this.userService
            .getAdmins()
            .pipe(skipWhile(() => this.site === null))
            .subscribe((admins: User[]) => (this.admins = admins));
          if (this.site.assignedAdminId) {
            this.assignedAdminId = this.site.assignedAdminId;
          }
        }
      });
  }

  isSiteSubmittedNotActive(): boolean {
    return this.site && this.site.activationDate === null && this.site.submittedDate !== null;
  }

  navigateByID(id: number): void {
    this.router.navigate(['admin', 'site', id]);
  }

  hasOverlaps(): boolean {
    return this.site.siteOverlaps.length > 0;
  }

  hasParcelOverlap(): boolean {
    return this.site.parcelOverlaps;
  }

  hasStandOverage(): boolean {
    return this.site.standOverage > 0.0;
  }

  hasValuationIssue(): boolean {
    return (
      this.site.valuation &&
      round(parseFloat(this.site.valuation.split(',')[0]), 1) !== round(this.site.valuationCheck, 1)
    );
  }

  changeApplicant(): void {
    const dialogRef = this.dialog.open(DialogChangeApplicantComponent, {
      panelClass: 'fc-dialog-change-owner',
      data: {
        currentApplicant: this.site.applicant,
        siteName: this.site.name,
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.user) {
        this.siteService.updateSiteApplicant(this.site.id, result.user.id).subscribe(() => {
          this.store.dispatch(setActiveSite({ id: this.site.id }));
        });
      }
    });
  }

  changeSiteMetadata(): void {
    this.dialog.open(DialogFormComponent, {
      data: {
        title: "Edit Site Metadata",
        fields: {
          name: {
            label: "Site Name",
            inputType: "string",
            control: new FormControl(this.site.name, [Validators.required, Validators.maxLength(50), Validators.pattern(RegexConstants.SITE_NAME)]),
            patternErrorText: "Your site name can only contain letters, numbers, and these characters: - + ( ) _"
          },
          namesOnDeed: {
            label: "Names on Deed",
            inputType: "string",
            control: new FormControl(this.site.namesOnDeed)
          },
          forestryFirm: {
            label: "Forestry Firm",
            inputType: "string",
            control: new FormControl(this.site.forestryFirm)
          },
          mortgageInformation: {
            label: "Mortgage Information",
            inputType: "textarea",
            control: new FormControl(this.site.mortgageInformation)
          }
        }
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.siteService.updateSiteMetadata(this.site.id, new Site(result))
          .subscribe({
            next: updatedSite => {
              this.store.dispatch(setActiveSiteSuccess({ data: updatedSite }));
            },
            error: err => {
              this.toastService.sendMessage(`Error saving metadata ${err}`, ToastLevel.WARN);
            }
          })
      }
    });
  }

  get projectedNext12MonthRevenue() {
    return this.formatHelper.formatMinMaxValuation(this.site.getAverageAnnualRevenue());
  }

  clearOriginalSubmitDate() {
    this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'Clear Original Submission Date',
        line1: 'This will remove the original submission date for this site and force the landowner to restart their date with the current date.',
        line2: 'Do you want to continue?',
        buttonTextYes: 'Yes, Clear',
        buttonTextNo: 'No, Cancel',
      },
      autoFocus: false,
    }).afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.siteService.changeSitesState([this.site.id], null, true).subscribe(() => {
          this.store.dispatch(setActiveSite({ id: this.site.id }));
        });
      }
    });

  }

  updateCustomOffer() {
    const dialogRef = this.dialog.open(DialogEditCustomOfferComponent, {
      panelClass: 'fc-dialog-change-custom-offer',
      autoFocus: false,
      data: {
        customOffer: this.site.availableEnrollmentGroups.find(group => group.isCustom),
        site: this.site,
      }
    });
    dialogRef.afterClosed().subscribe((result: EnrollmentGroup) => {
      if (result) {
        this.enrollmentGroupService.updateCustomEnrollmentGroupForSite(this.site.id, result).subscribe(updatedSite => {
          this.store.dispatch(setActiveSiteSuccess({ data: updatedSite }));
        });
      }
    });
  }

  changeEnrollmentGroup() {
    const customGroup = this.site.availableEnrollmentGroups.find(g => g.isCustom);
    this.siteService.getAvailableEnrollmentGroups(this.site.id).subscribe(groups => {
      this.dialog.open(DialogSelectOptionComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          options: groups.map(group => {
            if (group.isCustom) {
              return group.name + '-Custom Terms';
            }
            if (customGroup && group.name === customGroup.name) {
              return group.name + '-Base Terms';
            }
            return group.name;
          }).sort(),
          title: 'Enrollment Group Select',
          line1: 'Please select the NEW enrollment group for this site',
          buttonTextYes: 'Apply',
          buttonTextNo: 'Cancel',
        },
        autoFocus: false,

      }).afterClosed().subscribe((result: string) => {
        if (result) {
          let isCustom = result.includes('-Custom Terms');
          let newGroup = isCustom ? customGroup : groups.find(group => group.name === result.replace('-Base Terms', ''));
          // Will new group require a change in date?!
          let submitDate = this.site.submittedDate || this.site.originalSubmittedDate;
          if (submitDate && !(newGroup.enrollmentStartDate <= submitDate && newGroup.enrollmentEndDate >= submitDate)) {
            this.dialog.open(DialogYesNoComponent, {
              panelClass: 'fc-yes-no-dialog',
              data: {
                title: 'Confirmation',
                line1: `By selecting this enrollment group, you will be CHANGING the submission date for this project from:${this.site.submittedDate || this.site.originalSubmittedDate} to ${newGroup.enrollmentStartDate}`,
                line2: this.site.submittedDate ? 'Additionally, this will remove the original Enrollment group and re-create all payment periods with the new terms.' : '',
                line3: 'This is not reversible! Do you want to continue?',
                buttonTextYes: 'Yes, Change',
                buttonTextNo: 'No, Cancel',
              },
              autoFocus: false,
            }).afterClosed().subscribe((result: boolean) => {
              if (result) {
                this.updateGroupAndSetSiteActive(newGroup.id, isCustom);
              }
            });
          } else if (this.site.submittedDate) {
            this.dialog.open(DialogYesNoComponent, {
              panelClass: 'fc-yes-no-dialog',
              data: {
                title: 'Confirmation',
                line1: 'This will remove the original Enrollment group and re-create all payment periods with the new terms.',
                line2: 'Do you want to continue?',
                buttonTextYes: 'Yes, Clear',
                buttonTextNo: 'No, Cancel',
              },
              autoFocus: false,
            }).afterClosed().subscribe((result: boolean) => {
              if (result) {
                this.updateGroupAndSetSiteActive(newGroup.id, isCustom);
              }
            });
          } else {
            // change
            this.updateGroupAndSetSiteActive(newGroup.id, isCustom);
          }
        }
      });
    });

  }

  updateGroupAndSetSiteActive(newGroupId: number, isCustom: boolean) {
    this.siteService.updateEnrollmentGroup(this.site.id, newGroupId, isCustom).subscribe({
      next: savedSite => {
        this.store.dispatch(setActiveSiteSuccess({ data: savedSite }));
      },
      error: error => this.store.dispatch(updateSiteFailure({ error }))
    });
  }
}
